import { ChangeEvent, FC, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import ModalDialog from '../../../../common/components/ModalDialog';
import ButtonGroup from '../../../../common/components/ButtonGroup';
import ExclusionComment from '../../../../elements/ExclusionComment/ExclusionComment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import InfoIcon from '../../../../elements/InfoIcon/InfoIcon';
import dayjs, { Dayjs } from 'dayjs';

const ExcludeIssueModal: FC<
  React.PropsWithChildren<ExcludeIssueModalProps>
> = ({
  open,
  onCloseExcludeIssue,
  handleExcludeIssue,
  isLoading,
  issueName,
  children,
  appName,
  excludeCategoryName,
  excludeRule,
  excludeComment,
  setExcludeComment,
  handleSnoozeDateChange,
}) => {
  const theme = useTheme();
  const { classes } = useStyles();
  const [date, setDate] = useState<Dayjs | null>(dayjs(null));
  const [never, setNever] = useState(true);
  const [error, setError] = useState('');

  const handleClickNever = () => {
    if (!never) {
      handleSnoozeDateChange();
    }
    setDate(null);
    setNever(!never);
  };

  const handleChangeDate = (newDate: Dayjs | null) => {
    if (never) {
      setDate(null);
    } else {
      if (newDate && isNaN(newDate.valueOf())) {
        setError('Please select a valid date');
      } else {
        setError('');
        setDate(newDate);
      }
    }
    handleSnoozeDateChange(newDate?.toDate());
  };

  return (
    <ModalDialog
      modalColor={theme.palette.info.main}
      aria-label='Exclude Issue'
      open={open}
      onClose={onCloseExcludeIssue}
      modalHeader='Exclude Issue'>
      {children}
      <Box flexGrow={1} mb={2}>
        <Typography
          color={theme.palette.text.primary}
          variant='body1'
          component='div'>
          {excludeCategoryName === 'Rule for this app only' ? (
            <>
              <p>
                Are you sure you want to exclude the rule "{excludeRule}" that
                generated the following issue:
              </p>
              <p style={{ fontWeight: 'bold' }}>{issueName}</p>
            </>
          ) : (
            <>
              <p>Are you sure you want to exclude the following issue?</p>
              <p style={{ fontWeight: 'bold' }}>{issueName}</p>
            </>
          )}
          {excludeCategoryName === 'Secret(s) across all apps' ? (
            <p>Please note that this may affect multiple applications.</p>
          ) : (
            <p>
              Please note that this exclusion will affect only the application "
              {appName}".
            </p>
          )}
        </Typography>
      </Box>
      <ExclusionComment
        excludeComment={excludeComment}
        setExcludeComment={setExcludeComment}
        message='Comment for this issue:'
      />
      <div className={classes.snoozeIssue}>
        <Typography
          color='text.primary'
          variant='body1'
          component='div'
          alignItems='center'
          display='flex'>
          Expires:
          <InfoIcon
            title={'By default exclusions will never expire'}
            marginX={0.5}
          />
        </Typography>
        <FormControlLabel
          control={<Checkbox onChange={handleClickNever} checked={never} />}
          label='Never'
          sx={{ width: '100px' }}
        />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            disabled={never}
            minDate={dayjs(new Date())}
            inputFormat='MM/DD/YYYY'
            value={date}
            onChange={handleChangeDate}
            componentsProps={{
              actionBar: {
                actions: ['clear', 'accept'],
              },
            }}
            renderInput={params => (
              <TextField {...params} sx={{ width: '50%' }} />
            )}
          />
          {error && (
            <Typography variant='body2' color='text.primary'>
              {error}
            </Typography>
          )}
        </LocalizationProvider>
      </div>
      <Box display='flex' alignItems='end'>
        <ButtonGroup
          onCancel={onCloseExcludeIssue}
          onClick={handleExcludeIssue}
          buttonCancelLabel='Cancel'
          buttonCancelTooltip='Click to cancel excluding'
          buttonActionLabel='Exclude issue'
          buttonActionTooltip='Click to exclude issue'
          isActionDisabled={false}
          isLoading={isLoading}
        />
      </Box>
    </ModalDialog>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  snoozeIssue: {
    display: 'flex',
    flexDirection: 'column',
    marginBlockEnd: theme.spacing(2),
  },
}));
export interface ExcludeIssueModalProps {
  open: boolean;
  onCloseExcludeIssue: () => void;
  handleExcludeIssue: () => void;
  isLoading: boolean;
  appName?: string;
  issueName?: string;
  excludeCategoryName?: string;
  excludeRule?: string;
  excludeComment?: string;
  setExcludeComment: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  handleSnoozeDateChange: (value?: Date) => void;
}

export default ExcludeIssueModal;
